import React, { memo, useCallback, useRef } from 'react';
import { Fab, Tooltip, List, ListItemText, Theme } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { OverflowTip } from '../overflow-tip';
import { AnprThemeColor } from 'shell/types/anpt-theme-color';

type UseStyleProps = {
  color: AnprThemeColor;
  variant: 'circular' | 'extended';
};

const useStyles = ({ color, variant }: UseStyleProps) =>
  makeStyles((theme: Theme) => {
    const mainColor = theme.palette[color].main || theme.palette.primary.main;
    const contrastColor = theme.palette[color].contrastText || theme.palette.primary.contrastText;
    return createStyles({
      fab: {
        color: `${contrastColor} !important`,
        backgroundColor: `${mainColor} !important`,
        padding: variant === 'extended' ? '1em !important' : '0',
      },
      notifyIcon: {
        color: `${mainColor} !important`,
      },
      filename: {
        fontWeight: 'bold',
      },
    });
  });

interface CommonProps {
  size?: 'small' | 'medium' | 'large';
  color?: AnprThemeColor;
  disabledColor?: AnprThemeColor;
  variant?: 'circular' | 'extended';
  label?: string;
  showFilename?: boolean;
  accept?: string;
  hideList?: boolean;
  disabled?: boolean;
  id?: string;
}

interface SingleFileProps extends CommonProps {
  file: File | undefined;
  multiple: never | false;
  onChange: (payload: File) => void;
  disabled?: boolean;
  tipMaxSize?: number;
}

interface MultiplFileProps extends CommonProps {
  files: File[] | undefined;
  multiple: true;
  onChange: (payload: File[]) => void;
  disabled?: boolean;
  tipMaxSize?: number;
}

export type InputFileProps = SingleFileProps | MultiplFileProps;

const InputFileComponent = ({
  onChange,
  size = 'small',
  color = 'primary',
  disabledColor = 'primary',
  variant = 'circular',
  label = '',
  multiple = false,
  showFilename = true,
  accept = '*',
  hideList = false,
  disabled = false,
  tipMaxSize = 200,
  ...props
}: InputFileProps): React.ReactElement => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const colorInput: AnprThemeColor = disabled ? disabledColor : color;

  const styles = useStyles({ color: colorInput, variant })();

  const file = 'file' in props ? props.file : undefined;
  const files = 'files' in props ? props.files : undefined;

  /**
   * La funzione triggera il click
   * dell'input nascosto
   */
  const onClick = useCallback((e) => {
    if (inputRef.current && inputRef.current.onchange) {
      inputRef.current.onchange(e);
    }
  }, []);

  /**
   * La funzione pulisce l'input nascosto
   */
  const resetInputFile = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [inputRef.current]);

  /**
   * La funzione si occupa di convertire un oggetto di tipo FileList
   * in una lista di File
   */
  const extractFileList = useCallback((fileList: FileList) => {
    const _files: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const _file = fileList.item(i);
      if (_file) {
        _files.push(_file);
      }
    }
    return _files;
  }, []);

  /**
   * La funzione si occupa gestire il caricamento dei file
   * Verifica la presenza di file caricati
   * Salva localmente i file caricati
   * Notifica il chiamante dei file caricati
   * @param event evento generato dall'input
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const uploadedFiles = extractFileList(event.target.files);
        resetInputFile();
        if (multiple) {
          (onChange as (payload: File[]) => void)(uploadedFiles);
        } else {
          (onChange as (payload: File) => void)(uploadedFiles[0]);
        }
      }
    },
    [onChange, resetInputFile, multiple, extractFileList]
  );

  const shownLabel = variant === 'extended' ? label : null;

  let filenameArea = null;

  if (showFilename && multiple && !hideList) {
    filenameArea = (
      <Tooltip
        arrow
        title={
          <List>
            {files?.map((_file) => (
              <ListItemText key={_file.name} primary={_file.name} />
            ))}
          </List>
        }
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          File caricati <InfoIcon className={styles.notifyIcon} sx={{ ml: 1 }} />
        </span>
      </Tooltip>
    );
  }

  if (showFilename && !multiple) {
    filenameArea = (
      <OverflowTip tipMaxSize={tipMaxSize} tooltipMessage={file?.name || ''}>
        {file?.name}
      </OverflowTip>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <label htmlFor={props.id} style={{ margin: '0 0.5em' }}>
        <input
          ref={inputRef}
          onChange={handleChange}
          id={props.id}
          name={props.id}
          hidden
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
        />
        <Fab
          className={styles.fab}
          onClick={onClick}
          disabled={disabled}
          size={size}
          component="span"
          aria-label="upload-file"
          variant={variant}
        >
          <AddIcon sx={{ mr: variant === 'extended' ? 1 : 0 }} />
          {shownLabel}
        </Fab>
      </label>
      {filenameArea}
    </span>
  );
};

InputFileComponent.displayName = 'Input file component';

export const PmiInputFile = memo(InputFileComponent) as typeof InputFileComponent;

export default InputFileComponent;
